import type { ErrorHandler } from '@angular/core'

/**
 * Browser-specific messages for a failed lazy ES-module import. A client on an
 * old bundle requesting a hashed chunk that no longer exists after a redeploy
 * surfaces one of these (Chrome/Vite, Firefox, Safari respectively).
 */
const CHUNK_LOAD_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
] as const

/** True when `error` is a failed lazy dynamic-import (stale chunk after redeploy). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return CHUNK_LOAD_MESSAGES.some((m) => message.includes(m))
}

/** Injected seams so the reload guard is unit-testable without a real window. */
export interface ReloadEnv {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  reload: () => void
  now: () => number
  /**
   * Holder for "we triggered a reload during THIS page load". Unlike the
   * sessionStorage cooldown marker it does NOT survive a real reload, which is
   * what lets `reloadOnceIfStaleChunk` tell a rejection from an already
   * self-healing incident (drop) apart from a chunk still missing after an
   * actual reload (report — the marker was inherited, the flag was not, #1533).
   * Optional: lazily attached when absent so a bare env keeps working.
   */
  pageLoad?: { reloaded: boolean }
}

/** sessionStorage key holding the epoch-ms of our last self-triggered reload. */
const RELOAD_KEY = 'tivedo.chunkReloadAt'

/** Suppress a repeat reload for this long so a genuinely-missing chunk can't loop. */
const RELOAD_WINDOW_MS = 10_000

/**
 * Page-load-scoped reload flag for production. Module state is exactly
 * page-load-scoped in the browser — a real reload re-evaluates the module and
 * resets it — while the sessionStorage marker persists across reloads. All
 * seams share it via `defaultReloadEnv()`, mirroring the shared marker.
 */
const PAGE_LOAD = { reloaded: false }

/**
 * Reload once to pick up the freshly-deployed bundle. Returns whether a reload
 * was triggered. Suppresses a second reload within a short window so a chunk
 * that is genuinely gone (not just stale) can't put the tab in a reload loop.
 */
export function reloadOnceForChunkError(env: ReloadEnv): boolean {
  const pageLoad = (env.pageLoad ??= { reloaded: false })
  const last = Number(env.storage.getItem(RELOAD_KEY))
  const now = env.now()
  if (Number.isFinite(last) && last > 0 && now - last < RELOAD_WINDOW_MS) {
    return false // we just reloaded and the chunk is still missing — don't loop
  }
  env.storage.setItem(RELOAD_KEY, String(now))
  pageLoad.reloaded = true
  env.reload()
  return true
}

/**
 * Router `withNavigationErrorHandler` hook: a failed lazy-route import (stale
 * chunk) reloads once to fetch the new bundle. Non-chunk navigation errors are
 * left untouched so Angular still surfaces them to the ErrorHandler (Sentry).
 */
export function handleNavigationError(error: unknown, env?: ReloadEnv): void {
  // `env` is resolved lazily (only on an actual chunk error) so module-eval and
  // any non-browser context never touch sessionStorage/location.
  if (isChunkLoadError(error)) reloadOnceForChunkError(env ?? defaultReloadEnv())
}

/**
 * Sentry `beforeSend` seam for the stale-chunk trap on the GLOBAL path. This app is
 * zoneless (`provideZonelessChangeDetection`), so a failed lazy `import()` that is NOT
 * a router navigation — a nested widget/chart import, a preload, an event-handler
 * import — rejects as a `window` unhandledrejection. With no zone.js it never reaches
 * Angular's `ErrorHandler` nor the router's NavigationError hook (both already covered,
 * #302/#311); Sentry's global handlers capture it and run it through `beforeSend`, the
 * one place the app can see it. Reload once (guarded) to pick up the new bundle.
 *
 * @returns `true` when the incident is self-healed — a reload was triggered now, or one
 * already fired earlier in THIS page load (a single stale bundle throws one rejection
 * per failed sibling import; the one reload fetches the bundle that resolves them all,
 * #1533) — the caller drops the Sentry event. `false` for a non-chunk error, or a chunk
 * error suppressed by the cooldown with no reload this page load (marker inherited from
 * a prior page load's reload ⇒ the chunk survived a real reload, genuinely gone) — the
 * caller reports it so a broken deploy stays visible.
 */
export function reloadOnceIfStaleChunk(error: unknown, env?: ReloadEnv): boolean {
  if (!isChunkLoadError(error)) return false
  const resolved = env ?? defaultReloadEnv()
  if (reloadOnceForChunkError(resolved)) return true
  return resolved.pageLoad?.reloaded === true
}

/**
 * Wraps the app's real ErrorHandler (Sentry): chunk-load errors that reach the
 * global handler (e.g. a non-route dynamic import) trigger a one-time reload and
 * are NOT reported; every other error is forwarded to the delegate unchanged.
 */
export function createChunkAwareErrorHandler(
  delegate: ErrorHandler,
  env?: ReloadEnv
): ErrorHandler {
  return {
    handleError(error: unknown): void {
      if (isChunkLoadError(error)) {
        // Resolve `env` lazily here, never at construction time — the provider
        // is built during app config where touching browser globals is unsafe.
        reloadOnceForChunkError(env ?? defaultReloadEnv())
        return
      }
      delegate.handleError(error)
    },
  }
}

/** Production `ReloadEnv` bound to the real browser globals. */
export function defaultReloadEnv(): ReloadEnv {
  return {
    storage: sessionStorage,
    reload: () => location.reload(),
    now: () => Date.now(),
    pageLoad: PAGE_LOAD,
  }
}
